import { QuoteExpirationAnchorEnum } from '@/gen/cortado/v1/settings_pb'
import type { Organization } from '@/gen/cortado/v1/organizations_pb'
import type { OrganizationEventType } from '@/gen/cortado/v1/settings_pb'
import { earliestEventDate } from '@/lib/quote-payment-schedule-defaults'

export const DEFAULT_QUOTE_VALIDITY_DAYS = 7
export const MIN_QUOTE_VALIDITY_DAYS = 1
export const MAX_QUOTE_VALIDITY_DAYS = 90
export const MIN_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS = 0
export const MAX_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS = 365

export interface QuoteExpirationRule {
  anchor: QuoteExpirationAnchorEnum
  offsetDays: number
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addLocalDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return startOfLocalDay(result)
}

export function normalizeQuoteValidityDays(days: number | undefined | null): number {
  if (days == null || days < MIN_QUOTE_VALIDITY_DAYS || days > MAX_QUOTE_VALIDITY_DAYS) {
    return DEFAULT_QUOTE_VALIDITY_DAYS
  }
  return days
}

export function normalizeQuoteExpirationBeforeEventDays(days: number | undefined | null): number {
  if (days == null || days < MIN_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS || days > MAX_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS) {
    return DEFAULT_QUOTE_VALIDITY_DAYS
  }
  return days
}

export function normalizeQuoteExpirationAnchor(
  anchor: QuoteExpirationAnchorEnum | undefined | null,
): QuoteExpirationAnchorEnum {
  switch (anchor) {
    case QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT:
      return QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT
    default:
      return QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_AFTER_CREATE
  }
}

export function normalizeQuoteExpirationOffsetDays(
  anchor: QuoteExpirationAnchorEnum,
  days: number | undefined | null,
): number {
  if (anchor === QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT) {
    return normalizeQuoteExpirationBeforeEventDays(days)
  }
  return normalizeQuoteValidityDays(days)
}

export function orgQuoteExpirationRule(organization: Organization | undefined | null): QuoteExpirationRule {
  const anchor = normalizeQuoteExpirationAnchor(organization?.defaultQuoteExpirationAnchor)
  return {
    anchor,
    offsetDays: normalizeQuoteExpirationOffsetDays(anchor, organization?.defaultQuoteValidityDays),
  }
}

export function hasQuoteExpirationOverride(
  eventType: OrganizationEventType | undefined | null,
): boolean {
  return !!eventType?.quoteExpirationAnchor && eventType.quoteExpirationOffsetDays != null
}

export function eventTypeQuoteExpirationOverride(
  eventType: OrganizationEventType | undefined | null,
): QuoteExpirationRule | undefined {
  if (!hasQuoteExpirationOverride(eventType)) {
    return undefined
  }
  const anchor = normalizeQuoteExpirationAnchor(eventType!.quoteExpirationAnchor)
  return {
    anchor,
    offsetDays: normalizeQuoteExpirationOffsetDays(anchor, eventType!.quoteExpirationOffsetDays),
  }
}

export function quoteExpirationOverrideFormDefaults(
  eventType: OrganizationEventType | undefined | null,
) {
  const overrideEnabled = hasQuoteExpirationOverride(eventType)
  const anchor = normalizeQuoteExpirationAnchor(eventType?.quoteExpirationAnchor)
  const offsetDays = normalizeQuoteExpirationOffsetDays(anchor, eventType?.quoteExpirationOffsetDays)

  return {
    overrideEnabled,
    anchor,
    offsetDays,
  }
}

export type QuoteExpirationOverrideFormState = ReturnType<typeof quoteExpirationOverrideFormDefaults>

export function isQuoteExpirationOverrideFormDirty(
  current: QuoteExpirationOverrideFormState,
  saved: QuoteExpirationOverrideFormState,
): boolean {
  if (current.overrideEnabled !== saved.overrideEnabled) {
    return true
  }
  if (!current.overrideEnabled) {
    return false
  }
  return current.anchor !== saved.anchor || current.offsetDays !== saved.offsetDays
}

export function quoteExpirationOverrideWriteValues(
  overrideEnabled: boolean,
  anchor: QuoteExpirationAnchorEnum,
  offsetDays: number,
) {
  if (!overrideEnabled) {
    return {
      quoteExpirationAnchor: QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_UNSPECIFIED,
      quoteExpirationOffsetDays: 0,
    }
  }
  return {
    quoteExpirationAnchor: anchor,
    quoteExpirationOffsetDays: offsetDays,
  }
}

export function quoteExpirationOffsetDaysBounds(anchor: QuoteExpirationAnchorEnum): {
  min: number
  max: number
} {
  if (anchor === QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT) {
    return {
      min: MIN_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS,
      max: MAX_QUOTE_EXPIRATION_BEFORE_EVENT_DAYS,
    }
  }
  return {
    min: MIN_QUOTE_VALIDITY_DAYS,
    max: MAX_QUOTE_VALIDITY_DAYS,
  }
}

export function validateQuoteExpirationOffsetDays(
  anchor: QuoteExpirationAnchorEnum,
  days: number,
): { valid: true } | { valid: false; min: number; max: number } {
  const { min, max } = quoteExpirationOffsetDaysBounds(anchor)
  if (days < min || days > max) {
    return { valid: false, min, max }
  }
  return { valid: true }
}

export function resolveQuoteExpirationRule(
  organization: Organization | undefined | null,
  eventTypeKey: string | undefined | null,
  eventTypes: OrganizationEventType[] | undefined,
): QuoteExpirationRule {
  const orgRule = orgQuoteExpirationRule(organization)
  if (!eventTypeKey || !eventTypes?.length) {
    return orgRule
  }
  const eventType = eventTypes.find((type) => type.key === eventTypeKey)
  return eventTypeQuoteExpirationOverride(eventType) ?? orgRule
}

export function defaultQuoteValidUntil(
  now: Date,
  rule: QuoteExpirationRule,
  eventDates: Array<Date | undefined | null>,
): Date {
  const today = startOfLocalDay(now)
  const offsetDays = normalizeQuoteExpirationOffsetDays(rule.anchor, rule.offsetDays)
  if (rule.anchor === QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT) {
    const earliest = earliestEventDate(eventDates)
    if (!earliest) {
      return addLocalDays(today, normalizeQuoteValidityDays(offsetDays))
    }
    const candidate = addLocalDays(earliest, -offsetDays)
    return candidate < today ? today : candidate
  }

  const candidate = addLocalDays(today, offsetDays)
  return candidate < today ? today : candidate
}

export function formatQuoteExpirationRule(rule: QuoteExpirationRule): string {
  if (rule.anchor === QuoteExpirationAnchorEnum.QUOTE_EXPIRATION_ANCHOR_BEFORE_EVENT) {
    if (rule.offsetDays === 0) {
      return 'Expires on the first event date'
    }
    return `Expires ${rule.offsetDays} day${rule.offsetDays === 1 ? '' : 's'} before the first event`
  }
  return `Expires ${rule.offsetDays} day${rule.offsetDays === 1 ? '' : 's'} after the quote is created`
}
